import React, { useState, useEffect, useMemo } from 'react'
import i18n from 'i18n'
import { useTranslation } from 'react-i18next'
import { OmDialog, OmButton, OmSelect, OmInputNumber, OmRadio, OmRadioGroup } from '@owma/kit'
import CalendarDatePicker from 'pages/todo/components/DatePicker'

import classNames from 'classnames'
import styles from './style.module.scss'
import { RRule, Weekday, ALL_WEEKDAYS } from 'rrule'
import calendarIcon from 'assets/images/calendar/icon-calendar-24.svg'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import backIcon from 'assets/images/toolbar-back.svg'
import * as dateUtils from 'pages/calendar/utils/DateUtils'
import { convertToRRuleOptions } from 'pages/calendar/utils/rruleCompat'

import dayjs from 'dayjs'

interface RecurrenceOptions {
  freq?: number
  interval?: number
  byweekday?: Weekday | Weekday[]
  bymonth?: number | number[]
  bymonthday?: number | number[]
  bysetpos?: number
  until?: Date | number | null
  count?: number | null
  dtstart?: Date
}

interface RecurrenceEditorProps {
  open: boolean
  onClose: () => void
  onSave: (options: RecurrenceOptions | null, start?: Date) => void
  startDate: Date
  options?: RecurrenceOptions
}

interface FreqOption {
  value: number
  label: string
}

interface MonthOption {
  value: number
  label: string
}

interface WeekdayOption {
  value: Weekday
  label: string
}

interface WeekdaySelectOption {
  value?: string
  label?: string
  type?: 'divider'
}

interface OrdinalOption {
  value: number
  label: string
}

interface EndOption {
  value: 'until' | 'count' | 'never'
  label: string
}

const RecurrenceEditor: React.FC<RecurrenceEditorProps> = ({
  open,
  onClose,
  onSave,
  startDate,
  options: rawOptions,
}) => {
  // node-ical 출처 RRule의 origOptions/options 는 camelCase 키 + string freq 형식이라
  // 아래 모든 옵션 접근(byweekday/bymonth/numeric freq 등)이 미스매칭된다. 진입부에서 한 번 정규화.
  const options = useMemo<RecurrenceOptions | undefined>(
    () =>
      rawOptions
        ? (convertToRRuleOptions(
            rawOptions as unknown as Record<string, unknown>,
          ) as unknown as RecurrenceOptions)
        : rawOptions,
    [rawOptions],
  )

  const FREQ_OPTIONS = useMemo<FreqOption[]>(
    () => [
      { value: RRule.DAILY, label: i18n.t('calendar.frequency.day') },
      { value: RRule.WEEKLY, label: i18n.t('calendar.frequency.week') },
      { value: RRule.MONTHLY, label: i18n.t('calendar.frequency.month') },
      { value: RRule.YEARLY, label: i18n.t('calendar.frequency.year') },
    ],
    [],
  )

  const MONTH_OPTIONS = useMemo<MonthOption[]>(
    () => [
      { value: 1, label: i18n.t('calendar.month.january') },
      { value: 2, label: i18n.t('calendar.month.february') },
      { value: 3, label: i18n.t('calendar.month.march') },
      { value: 4, label: i18n.t('calendar.month.april') },
      { value: 5, label: i18n.t('calendar.month.may') },
      { value: 6, label: i18n.t('calendar.month.june') },
      { value: 7, label: i18n.t('calendar.month.july') },
      { value: 8, label: i18n.t('calendar.month.august') },
      { value: 9, label: i18n.t('calendar.month.september') },
      { value: 10, label: i18n.t('calendar.month.october') },
      { value: 11, label: i18n.t('calendar.month.november') },
      { value: 12, label: i18n.t('calendar.month.december') },
    ],
    [],
  )

  const WEEKDAY_OPTIONS = useMemo<WeekdayOption[]>(
    () => [
      { value: RRule.SU, label: i18n.t('calendar.weekday.sun') },
      { value: RRule.MO, label: i18n.t('calendar.weekday.mon') },
      { value: RRule.TU, label: i18n.t('calendar.weekday.tue') },
      { value: RRule.WE, label: i18n.t('calendar.weekday.wed') },
      { value: RRule.TH, label: i18n.t('calendar.weekday.thu') },
      { value: RRule.FR, label: i18n.t('calendar.weekday.fri') },
      { value: RRule.SA, label: i18n.t('calendar.weekday.sat') },
    ],
    [],
  )

  const WEEKDAY_SELECT_OPTIONS = useMemo<WeekdaySelectOption[]>(
    () => [
      { value: 'SU', label: i18n.t('calendar.weekday.sunday') },
      { value: 'MO', label: i18n.t('calendar.weekday.monday') },
      { value: 'TU', label: i18n.t('calendar.weekday.tuesday') },
      { value: 'WE', label: i18n.t('calendar.weekday.wednesday') },
      { value: 'TH', label: i18n.t('calendar.weekday.thursday') },
      { value: 'FR', label: i18n.t('calendar.weekday.friday') },
      { value: 'SA', label: i18n.t('calendar.weekday.saturday') },
      { type: 'divider' as const },
      { value: 'DAY', label: i18n.t('calendar.weekday.day') },
      { value: 'WEEKDAY', label: i18n.t('calendar.weekday.weekday') },
      { value: 'WEEKEND', label: i18n.t('calendar.weekday.weekend') },
    ],
    [],
  )

  const ORDINAL_OPTIONS = useMemo<OrdinalOption[]>(
    () => [
      { value: 1, label: i18n.t('calendar.ordinal.first') },
      { value: 2, label: i18n.t('calendar.ordinal.second') },
      { value: 3, label: i18n.t('calendar.ordinal.third') },
      { value: 4, label: i18n.t('calendar.ordinal.fourth') },
      { value: -1, label: i18n.t('calendar.ordinal.last') },
    ],
    [],
  )

  const END_OPTIONS = useMemo<EndOption[]>(
    () => [
      { value: 'until', label: i18n.t('calendar.end.until') },
      { value: 'count', label: i18n.t('calendar.end.count') },
      { value: 'never', label: i18n.t('calendar.end.never') },
    ],
    [],
  )

  const [freq, setFreq] = useState<number>(options ? (options.freq ?? RRule.WEEKLY) : RRule.WEEKLY)
  const [interval, setInterval] = useState<number>(options?.interval || 1)
  const [byweekday, setByweekday] = useState<Weekday[]>(
    options?.byweekday
      ? Array.isArray(options?.byweekday)
        ? options.byweekday
        : [options.byweekday]
      : [],
  )
  const [bymonth, setBymonth] = useState<number[]>(
    options?.bymonth ? (Array.isArray(options?.bymonth) ? options.bymonth : [options.bymonth]) : [],
  )
  const [start, setStart] = useState<Date>(startDate)

  const calcMonthlyType = (): 'day' | 'ordinal' => {
    if (options?.bysetpos) {
      return 'ordinal'
    } else if (options?.byweekday) {
      if (
        !Array.isArray(options.byweekday) &&
        (options.byweekday as Weekday & { n: number }).n > 0
      ) {
        return 'ordinal'
      } else if (Array.isArray(options.byweekday) && options.byweekday.length === 1) {
        return 'ordinal'
      } else {
        return 'day'
      }
    } else {
      return 'day'
    }
  }
  const [monthlyType, setMonthlyType] = useState<'day' | 'ordinal'>(calcMonthlyType())
  const [monthlyDay, setMonthlyDay] = useState<number>(
    options?.bymonthday
      ? Array.isArray(options?.bymonthday)
        ? options.bymonthday[0]
        : options.bymonthday
      : start.getDate(),
  )

  const calcDefaultOrdinalWeek = (): number => {
    const monthFirstDate = dateUtils.getMonthFirstDate(startDate)
    const firstWeekNumber = dateUtils.getWeekNumber(monthFirstDate)
    const aWeekNumber = dateUtils.getWeekNumber(startDate)
    const ordinalWeek = aWeekNumber - firstWeekNumber + 1
    return ordinalWeek > 4 ? -1 : ordinalWeek
  }

  const calcMonthlyOrdinal = (): number => {
    if (options?.bysetpos) {
      return options.bysetpos
    } else if (options?.byweekday) {
      if (!Array.isArray(options.byweekday)) {
        const n = (options.byweekday as Weekday & { n: number }).n
        if (n == null || n === 0) {
          return calcDefaultOrdinalWeek()
        } else {
          return n
        }
      }
      if (Array.isArray(options.byweekday) && options.byweekday.length === 1) {
        const n = (options.byweekday[0] as Weekday & { n: number }).n
        if (n == null || n === 0) {
          return calcDefaultOrdinalWeek()
        } else {
          return n
        }
      } else {
        return calcDefaultOrdinalWeek()
      }
    } else {
      return calcDefaultOrdinalWeek()
    }
  }
  const [monthlyOrdinal, setMonthlyOrdinal] = useState<number>(calcMonthlyOrdinal())

  const calcMonthlyWeekday = (): string => {
    if (options?.byweekday) {
      const inWeekday = options.byweekday
      if (Array.isArray(inWeekday)) {
        if (inWeekday.length === 7) {
          return 'DAY'
        } else if (inWeekday.length === 5) {
          return 'WEEKDAY'
        } else if (inWeekday.length === 2) {
          return 'WEEKEND'
        } else if (inWeekday.length === 1) {
          const cloneWeekday = new Weekday((inWeekday[0] as Weekday & { weekday: number }).weekday)
          return cloneWeekday.toString()
        } else {
          return 'DAY'
        }
      } else {
        const cloneWeekday = new Weekday((inWeekday as Weekday & { weekday: number }).weekday)
        return cloneWeekday.toString()
      }
    } else {
      let dayIndex = startDate.getDay() - 1
      dayIndex = dayIndex < 0 ? 6 : dayIndex
      return ALL_WEEKDAYS[dayIndex]
    }
  }
  const [monthlyWeekday, setMonthlyWeekday] = useState<string>(calcMonthlyWeekday())
  const [endType, setEndType] = useState<'until' | 'count' | 'never' | null>(null)
  const [until, setUntil] = useState<Date | number | null>(
    options ? (options.until ?? null) : new Date(startDate).setDate(startDate.getDate() + 7 * 12),
  )
  const [count, setCount] = useState<number | null>(null)
  const [startDatePickerOpen, setStartDatePickerOpen] = useState<boolean>(false)
  const [untilDatePickerOpen, setUntilDatePickerOpen] = useState<boolean>(false)

  const { t } = useTranslation()

  useEffect(() => {
    setStart(startDate)
  }, [startDate])

  useEffect(() => {
    if (options?.until) {
      setEndType('until')
      setUntil(options.until)
    } else if (options?.count) {
      setEndType('count')
      setCount(options.count)
    } else if (options) {
      setEndType('never')
      setUntil(null)
      setCount(null)
    } else {
      setEndType('until')
      setUntil(new Date(startDate).setDate(startDate.getDate() + 7 * 12))
      setCount(null)
    }
  }, [options, startDate])

  const _assignUntil = () => {
    if (freq === RRule.DAILY) {
      let startDate = new Date(start)
      startDate.setMonth(startDate.getMonth() + 1)
      if (startDate.getMonth() !== start.getMonth() + 1) {
        startDate.setDate(0)
      }
      setUntil(startDate)
    } else if (freq === RRule.WEEKLY) {
      const startDate = new Date(start)
      startDate.setDate(startDate.getDate() + 7 * 12)
      setUntil(startDate)
    } else if (freq === RRule.MONTHLY) {
      const startDate = new Date(start)
      startDate.setFullYear(startDate.getFullYear() + 1)
      setUntil(startDate)
    } else if (freq === RRule.YEARLY) {
      const startDate = new Date(start)
      startDate.setFullYear(startDate.getFullYear() + 5)
      setUntil(startDate)
    }
  }

  const _assignCount = () => {
    if (freq === RRule.DAILY) {
      setCount(30)
    } else if (freq === RRule.WEEKLY) {
      setCount(12)
    } else if (freq === RRule.MONTHLY) {
      setCount(12)
    } else if (freq === RRule.YEARLY) {
      setCount(5)
    }
  }

  useEffect(() => {
    if (endType === 'until' && !until) {
      _assignUntil()
    } else if (endType === 'count' && !count) {
      _assignCount()
    }
  }, [endType])

  useEffect(() => {
    if (freq === RRule.WEEKLY) {
      // 선택된 요일이 하나도 없을 때만 시작일 요일을 기본값으로 채운다.
      // 시작일 요일의 포함 여부로 판단하면 사용자가 고른 요일 위에 시작일 요일이 매번 끼어든다(OFM-268).
      if (byweekday.length === 0) {
        setByweekday([WEEKDAY_OPTIONS[start.getDay()].value])
      }
    } else if (freq === RRule.MONTHLY || freq === RRule.YEARLY) {
      setMonthlyDay(monthlyDay || start.getDate())
      if (freq === RRule.YEARLY) {
        if (!bymonth || bymonth.length === 0) {
          setBymonth([start.getMonth() + 1])
        } else {
          setBymonth(bymonth)
        }
      }
    }
    if (endType === 'until') {
      _assignUntil()
    } else if (endType === 'count') {
      _assignCount()
    }
  }, [freq])

  const handleEndTypeChange = (value: 'until' | 'count' | 'never') => {
    setEndType(value)
    if (value === 'never') {
      setUntil(null)
      setCount(null)
    }
  }

  const handleStartDateChange = (date: { toDate: () => Date }) => {
    const newDate = date.toDate()
    newDate.setHours(start.getHours())
    newDate.setMinutes(start.getMinutes())
    newDate.setSeconds(0)
    newDate.setMilliseconds(0)
    setStart(newDate)
  }

  const handleSave = () => {
    const newOptions: RecurrenceOptions = {
      freq,
      interval,
      dtstart: start,
    }

    if (freq === RRule.WEEKLY && byweekday.length > 0) {
      newOptions.byweekday = byweekday
    }

    if (freq === RRule.YEARLY && bymonth.length > 0) {
      newOptions.bymonth = bymonth[0]
    }

    if (freq === RRule.MONTHLY || freq === RRule.YEARLY) {
      if (monthlyType === 'day') {
        newOptions.bymonthday = monthlyDay
      } else {
        // ordinal type
        if (monthlyWeekday === 'DAY') {
          newOptions.byweekday = [
            RRule.SU,
            RRule.MO,
            RRule.TU,
            RRule.WE,
            RRule.TH,
            RRule.FR,
            RRule.SA,
          ]
          newOptions.bysetpos = monthlyOrdinal
        } else if (monthlyWeekday === 'WEEKDAY') {
          newOptions.byweekday = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR]
          newOptions.bysetpos = monthlyOrdinal
        } else if (monthlyWeekday === 'WEEKEND') {
          newOptions.byweekday = [RRule.SA, RRule.SU]
          newOptions.bysetpos = monthlyOrdinal
        } else {
          const weekday = (
            RRule as any as Record<string, Weekday & { nth: (n: number) => Weekday }>
          )[monthlyWeekday]
          newOptions.byweekday = [weekday]
          newOptions.bysetpos = monthlyOrdinal
        }
      }
    }

    if (endType === 'until' && until) {
      newOptions.until = until
    } else if (endType === 'count' && count && count > 0) {
      newOptions.count = count
    }

    onSave(newOptions, start)
    onClose()
  }

  const renderWeeklyOptions = () => {
    if (freq !== RRule.WEEKLY) return null

    return (
      <div className={styles.weekdaySelector}>
        {WEEKDAY_OPTIONS.map((day) => (
          <button
            key={(day.value as Weekday & { weekday: number }).weekday}
            className={`${styles.weekdayButton} ${
              byweekday.some(
                (d: Weekday) =>
                  (d as Weekday & { weekday: number }).weekday ===
                  (day.value as Weekday & { weekday: number }).weekday,
              )
                ? styles.selected
                : ''
            }`}
            onClick={() => {
              const isSelected = byweekday.some(
                (d: Weekday) =>
                  (d as Weekday & { weekday: number }).weekday ===
                  (day.value as Weekday & { weekday: number }).weekday,
              )
              if (isSelected) {
                // 주간 반복은 요일이 최소 1개 있어야 한다. 마지막 하나는 해제를 막는다(OFM-268).
                if (byweekday.length === 1) return
                setByweekday(
                  byweekday.filter(
                    (d: Weekday) =>
                      (d as Weekday & { weekday: number }).weekday !==
                      (day.value as Weekday & { weekday: number }).weekday,
                  ),
                )
              } else {
                setByweekday([...byweekday, day.value])
              }
            }}
          >
            {day.label}
          </button>
        ))}
      </div>
    )
  }

  const renderMonthlyOptions = () => {
    if (freq !== RRule.MONTHLY && freq !== RRule.YEARLY) return null

    return (
      <div className={styles.formRow}>
        <div className={styles.onLabel}>{''}</div>
        <div className={styles.monthlyOptions}>
          <OmRadioGroup
            value={monthlyType}
            onChange={(value) => setMonthlyType(value as 'day' | 'ordinal')}
          >
            <div className={styles.monthlyRow}>
              <OmRadio value="day">
                <OmInputNumber
                  min={1}
                  max={dayjs(start).endOf('month').date()}
                  value={monthlyDay}
                  onChange={(value) => setMonthlyDay(value ?? 1)}
                  disabled={monthlyType !== 'day'}
                />
                <span>{t('calendar.weekday.day')}</span>
              </OmRadio>
            </div>
            <div className={styles.monthlyRow}>
              <OmRadio value="ordinal" />
              <div className={styles.ordinalSelects}>
                <OmSelect
                  popupClassName="contextMenu"
                  value={monthlyOrdinal}
                  suffixIcon={
                    <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
                  }
                  onChange={setMonthlyOrdinal}
                  style={{ width: 120 }}
                  disabled={monthlyType !== 'ordinal'}
                  options={ORDINAL_OPTIONS}
                />
                <OmSelect
                  value={monthlyWeekday}
                  onChange={setMonthlyWeekday}
                  style={{ width: 120 }}
                  popupClassName="contextMenu"
                  disabled={monthlyType !== 'ordinal'}
                  suffixIcon={
                    <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
                  }
                  options={WEEKDAY_SELECT_OPTIONS.map((option) =>
                    option.type === 'divider'
                      ? {
                          value: '__divider__',
                          label: (
                            <div className={styles.selectDivider}>
                              <div className={styles.divider} />
                            </div>
                          ),
                          disabled: true,
                        }
                      : { value: option.value as string, label: option.label as string },
                  )}
                />
              </div>
            </div>
          </OmRadioGroup>
        </div>
      </div>
    )
  }

  const renderEndOptions = () => {
    return (
      <div className={styles.formRow}>
        <div className={styles.formLabel}>{t('calendar.edit.end')}</div>
        <div className={styles.endOptions}>
          <OmSelect
            popupClassName="contextMenu"
            value={endType ?? undefined}
            onChange={handleEndTypeChange}
            style={{ width: 120 }}
            suffixIcon={<img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />}
            options={END_OPTIONS}
          />
          {endType === 'until' && (
            <div className={styles.datePickerContainer}>
              <button
                className={styles.dateButton}
                onClick={() => setUntilDatePickerOpen(!untilDatePickerOpen)}
              >
                <span className={styles.dateValue}>
                  {until ? dayjs(until).format('YYYY-MM-DD') : 'Select date'}
                </span>
                <img src={calendarIcon} alt="Calendar" className={styles.chevronIcon} />
              </button>
              {untilDatePickerOpen && (
                <div
                  className={styles.datePickerOverlay}
                  onClick={() => setUntilDatePickerOpen(false)}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <CalendarDatePicker
                      value={until as any}
                      onChange={(date) => {
                        setUntil(date)
                        setUntilDatePickerOpen(false)
                      }}
                      onClose={() => setUntilDatePickerOpen(false)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {endType === 'count' && (
            <div className={styles.afterInput}>
              <OmInputNumber
                min={1}
                value={count ?? undefined}
                onChange={(value) => setCount(value)}
                style={{ width: 80 }}
              />
              <span>{t('calendar.edit.occurrences')}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  const titleElem = (
    <React.Fragment>
      <OmButton
        variant="text"
        icon={<img src={backIcon} alt="Back" />}
        className={classNames(styles.backButton, 'backButton')}
        onClick={onClose}
      />
      <span>{t('calendar.recurrence.customRecurrence1')}</span>
    </React.Fragment>
  )

  return (
    <OmDialog
      title={titleElem}
      open={open}
      onClose={onClose}
      closable={false}
      width={560}
      wrapClassName={classNames('recurrenceEditorModal')}
      className={classNames(styles.recurrenceEditor, 'mobile-fullpage')}
      footer={
        <div className={styles.modalFooter}>
          {options?.freq !== undefined && (
            <button className={styles.removeButton} onClick={() => onSave(null)}>
              {t('common.button.remove')}
            </button>
          )}
          <div className={styles.footerButtons}>
            <OmButton onClick={onClose}>{t('common.button.cancel')}</OmButton>
            <OmButton className={classNames('type1', 'h38', 'w100')} onClick={handleSave}>
              {t('common.button.save')}
            </OmButton>
          </div>
        </div>
      }
    >
      <div className={styles.recurrenceForm}>
        <div className={styles.formContent}>
          <div className={styles.formRow}>
            <div className={styles.formLabel}>{t('common.button.start')}</div>
            <div className={styles.datePickerContainer}>
              <button
                className={styles.dateButton}
                onClick={() => setStartDatePickerOpen(!startDatePickerOpen)}
              >
                <span className={styles.dateValue}>{dayjs(start).format('YYYY-MM-DD')}</span>
                <img src={calendarIcon} alt="Calendar" className={styles.chevronIcon} />
              </button>
              {startDatePickerOpen && (
                <div
                  className={styles.datePickerOverlay}
                  onClick={() => setStartDatePickerOpen(false)}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <CalendarDatePicker
                      value={start}
                      onChange={(date) => {
                        handleStartDateChange({ toDate: () => date })
                        setStartDatePickerOpen(false)
                      }}
                      onClose={() => setStartDatePickerOpen(false)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formLabel}>{t('calendar.edit.repeatEvery')}</div>
            <div className={styles.repeatInputs}>
              <OmInputNumber
                min={1}
                value={interval}
                onChange={(value) => setInterval(value ?? 1)}
              />
              <OmSelect
                popupClassName="contextMenu"
                value={freq}
                onChange={setFreq}
                suffixIcon={<img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />}
                options={FREQ_OPTIONS}
              />
              {freq === RRule.YEARLY && (
                <OmSelect
                  popupClassName="contextMenu"
                  value={bymonth[0] || start.getMonth() + 1}
                  onChange={(value) => setBymonth([value])}
                  style={{ width: 120 }}
                  suffixIcon={
                    <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
                  }
                  options={MONTH_OPTIONS}
                />
              )}
            </div>
          </div>
          {renderWeeklyOptions()}
          {renderMonthlyOptions()}
          {renderEndOptions()}
        </div>
      </div>
    </OmDialog>
  )
}

export default RecurrenceEditor
